"use client";

import Link from "next/link";
import { Check, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AuthCtaLink } from "./auth-cta-link";
import { defaultCountryConfig } from "@/config/counties/default";
import type { PricingConfig, PricingPlan } from "@/config/counties/types";
import { FREE_TIER_ENABLED } from "@/config/feature-flags";
import { FREE_MONTHLY_CONTACT_CAP } from "@/config/stripe-plans";
import { useEffect, useRef } from "react";
import posthog from "posthog-js";
import { trackFbInitiateCheckout, trackFbViewContent, trackFbCTAClick } from "@/lib/analytics/facebook-pixel";

export const plans: PricingPlan[] = defaultCountryConfig.pricing.plans;

type PricingSectionProps = {
  data: PricingConfig;
  /**
   * Suppress this section's own heading block.
   *
   * On the homepage the section is one band among many and needs its own
   * title. On /pricing it sits directly under the page's <h1>, where the two
   * stack into a doubled headline saying the same thing twice. The page keeps
   * the H1 (one per page) and turns this off.
   */
  hideHeading?: boolean;
};

export default function PricingSection({ data, hideHeading = false }: PricingSectionProps) {
  const sectionRef = useRef<HTMLElement>(null);
  const hasTrackedView = useRef(false);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasTrackedView.current) {
          hasTrackedView.current = true;
          trackFbViewContent({
            contentName: "Pricing Section",
            contentIds: data.plans.map((p) => p.priceId).filter(Boolean),
          });
          posthog.capture("pricing_viewed");
        }
      },
      { threshold: 0.3 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [data.plans]);

 const trackCheckout = (priceId: string, priceLabel: string, planName: string) => {
    const value = Number.parseFloat(priceLabel.replace(/[^0-9.]/g, ""));
    trackFbInitiateCheckout({
      contentIds: [priceId],
      ...(Number.isFinite(value) && { value, currency: "USD" }),
    });
    // ✅ Track CTA click
    trackFbCTAClick({
      buttonName: "Buy Now",
      location: `Pricing - ${planName}`,
    });
    posthog.capture("pricing_plan_selected", {
      price_id: priceId,
      plan_name: planName,
      ...(Number.isFinite(value) && { value, currency: "USD" }),
    });
  };

  return (
    <section id="pricing" ref={sectionRef} className="py-16 sm:py-24 bg-card">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {!hideHeading && (
          <div className="text-center mb-16 sm:mb-20">
            <h2 className="text-[26px] md:text-[36px] lg:text-[45px] font-bold text-ink mb-6">
              {data.title}
            </h2>
            <p className="text-muted-ink text-[14px] md:text-[16px] lg:text-[18px]">{data.subtitle}</p>
          </div>
        )}

        {/* Free-tier ribbon: the single Unlimited card kept the "no-tricks"
            positioning, but the 25-free-contacts tier was invisible on the
            pricing block itself. This surfaces it without adding a second card. */}
        {FREE_TIER_ENABLED && (
          <div className="max-w-2xl mx-auto mb-8 flex flex-col sm:flex-row items-center justify-between gap-4 rounded-2xl border border-primary/30 bg-primary/5 px-6 py-5">
            <div className="text-center sm:text-left">
              <p className="text-[15px] md:text-[17px] font-bold text-ink">
                Start free — {FREE_MONTHLY_CONTACT_CAP} contacts every month, no
                credit card
              </p>
              <p className="text-[13px] md:text-[14px] text-muted-ink mt-0.5">
                Try real, verified data before you pay. Upgrade any time.
              </p>
            </div>
            <AuthCtaLink
              href="/dashboard"
              onClick={() =>
                posthog.capture("pricing_free_cta_clicked", {
                  cap: FREE_MONTHLY_CONTACT_CAP,
                })
              }
              className="shrink-0 inline-flex items-center justify-center h-[44px] px-6 rounded-lg text-[14px] lg:text-[16px] font-medium text-primary bg-card border border-primary/40 hover:bg-primary/10 transition-colors whitespace-nowrap"
            >
              Start Free
            </AuthCtaLink>
          </div>
        )}

        <div className="flex justify-center max-w-2xl mx-auto">
          {data.plans.map((plan) => (
            <div
              key={plan.name}
              className={`bg-card rounded-2xl relative flex flex-col w-full max-w-md border ${plan.borderColor} p-8 lg:py-12 lg:px-10 lg:shadow-2xl`}
            >
              {plan.badge && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                  <div className="bg-primary-surface text-primary-foreground text-sm font-medium px-4 py-1.5 rounded-full flex items-center gap-1.5 whitespace-nowrap shadow-lg shadow-primary/25">
                    <Zap className="w-4 h-4 text-white" />
                    {plan.badge}
                  </div>
                </div>
              )}

              <div className="text-center mb-8">
                <h3 className="text-xl font-bold text-ink mb-2">{plan.name}</h3>
                <p className="text-muted-ink text-[14px] md:text-[16px] lg:text-[18px]">{plan.description}</p>
              </div>

              <div className="text-center mb-8">
                <span className="text-5xl font-bold text-ink">{plan.price}</span>
                <span className="text-muted-ink text-[14px] md:text-[16px] lg:text-[18px] ml-1">{plan.period}</span>
              </div>

              <ul className="space-y-4 mb-10 flex-1">
                {plan.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-3 text-muted-ink text-[14px] md:text-[16px] lg:text-[18px]">
                    <Check className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-auto">
                <Link href={`/checkout?priceId=${encodeURIComponent(plan.priceId)}`} className="w-full">
                  <Button
                    type="button"
                    className={`w-full py-6 text-[14px] md:text-[14px] lg:text-[16px] font-medium rounded-lg ${plan.buttonStyle}`}
                    style={plan.buttonGradient ? { background: plan.buttonGradient } : undefined}
                   onClick={() => trackCheckout(plan.priceId, plan.price, plan.name)}
                  >
                    {data.buyNowText}
                  </Button>
                </Link>
                {data.reassurance && (
                  <p className="mt-4 text-center text-xs text-subtle-ink leading-relaxed">
                    {data.reassurance}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>

        {data.pledge && (
          <div className="max-w-2xl mx-auto mt-10 rounded-2xl border border-line bg-surface-soft p-6 sm:p-8">
            <h3 className="text-center text-sm font-bold uppercase tracking-wide text-ink mb-5">
              {data.pledge.title}
            </h3>
            <ul className="grid gap-3 sm:grid-cols-2">
              {data.pledge.items.map((item) => (
                <li
                  key={item}
                  className="flex items-start gap-2.5 text-[14px] text-muted-ink"
                >
                  <Check className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </section>
  );
}
